import { type ReactNode } from 'react';
import {
  Info,
  Target,
  Rocket,
  CalendarRange,
  ClipboardList,
  HeartPulse,
  LayoutList,
  Calculator,
  Library,
  Users,
  FileText,
  ArrowRight,
} from 'lucide-react';
import { Card, CardBody, PageHeader } from '../../ui';
import { Logo } from '../../app/Logo';

/**
 * About / Overview — a single, scannable page that explains the whole product
 * to a new user. Every claim here maps to a real, shipped surface (see the
 * sidebar): Goals & Planning, the Sprint Board, Estimation, Knowledge & AI,
 * Team & Awareness, and Reporting. No feature is invented — this is a truthful
 * tour, grouped by area with short blurbs.
 */

/** The core product loop, rendered as a chip row. */
const CORE_LOOP = [
  'Create',
  'Estimate',
  'Plan',
  'Work',
  'Update',
  'Done',
  'Learn',
] as const;

interface Section {
  icon: ReactNode;
  heading: string;
  blurb: string;
  points: { label: string; detail: string }[];
}

const SECTIONS: Section[] = [
  {
    icon: <Target className="h-5 w-5" />,
    heading: 'Goals & Planning',
    blurb:
      'Set direction with first-class, trackable goals and trace every one down to the sprint work that delivers it.',
    points: [
      {
        label: 'Planning frameworks',
        detail:
          'BHAG (multi-year), Quarterly, and the Amazon operating-plan cycles — OP1 (the annual operating plan) and OP2 (its annual refresh) — plus Annual and Custom.',
      },
      {
        label: 'Hierarchy & health',
        detail:
          'Goals nest from Organization down to Sprint Story, carry an owned status (On track / At risk / Off track), and surface an advisory Goal Health signal you can accept or override.',
      },
      {
        label: 'Traceability',
        detail:
          'Each goal rolls up the estimates and sprint tasks linked to it, so progress reflects real engineering work — not a manual guess.',
      },
    ],
  },
  {
    icon: <LayoutList className="h-5 w-5" />,
    heading: 'Sprint Board',
    blurb:
      'Plan and run sprints on a board built for how engineering teams actually work.',
    points: [
      {
        label: 'Backlog → sprint',
        detail:
          'Groom a backlog, plan a sprint against team capacity, and move work across TODO / IN PROGRESS / DONE — with a Blocked state that stays visible.',
      },
      {
        label: 'Views that fit the team',
        detail:
          'Flip to a By-Person view, break stories into subtasks, and capture ad-hoc / unplanned scope that shows up mid-sprint.',
      },
    ],
  },
  {
    icon: <Calculator className="h-5 w-5" />,
    heading: 'Estimation',
    blurb:
      'A compact, transparent estimation engine that turns a few honest inputs into defensible story points.',
    points: [
      {
        label: 'The B + C + W engine',
        detail:
          'Pick a change type for a Base, add Complexity, Extra Work, and Unknowns → the engine derives a confidence level and a Fibonacci story-point estimate.',
      },
      {
        label: 'Stay in control',
        detail:
          'Override any result with a remark, or define a custom change type when the defaults do not fit.',
      },
      {
        label: 'Estimate with Vector',
        detail:
          'Optionally ground an estimate in the team knowledge base for an AI-assisted starting point — shown transparently alongside the manual engine, never instead of it.',
      },
    ],
  },
  {
    icon: <Library className="h-5 w-5" />,
    heading: 'Knowledge & AI',
    blurb:
      'A team knowledge base (Bedrock RAG) and a set of assistants that answer from what your team has actually recorded — with citations.',
    points: [
      {
        label: 'Chat & Ask Vector',
        detail:
          'Chat against the knowledge base with source citations, or Ask Vector a question and get an answer grounded in your team’s goals, work, docs, and decisions.',
      },
      {
        label: 'In-context assistants',
        detail:
          'Task KB Research suggests what a task will touch; Team Digest, Catch Me Up, and Sprint Insights summarize what changed and why.',
      },
      {
        label: 'AI structuring',
        detail:
          'Documents and workstreams can be AI-structured into a clean, consistent shape as you capture them.',
      },
    ],
  },
  {
    icon: <Users className="h-5 w-5" />,
    heading: 'Team & Awareness',
    blurb:
      'Keep everyone aligned without another status meeting.',
    points: [
      {
        label: 'Daily rhythm',
        detail:
          'A Daily Scrum assembled from task updates, Team Today, and Daily Updates keep the day’s picture current.',
      },
      {
        label: 'Context & connections',
        detail:
          'Teams, Workstreams, Blockers, a Change Feed, Decisions, an engineering Timeline, Services, Experts, and an entity Graph connect the dots across the team.',
      },
    ],
  },
  {
    icon: <FileText className="h-5 w-5" />,
    heading: 'Reporting',
    blurb: 'Share progress without hand-assembling a deck.',
    points: [
      {
        label: 'One-page sprint summary',
        detail:
          'Generate a clean, one-page PDF summary of a sprint straight from the board.',
      },
    ],
  },
];

/** The framework chips shown in the Goals & Planning highlight. */
const FRAMEWORKS: { icon: ReactNode; label: string; hint: string }[] = [
  { icon: <Rocket className="h-4 w-4" />, label: 'BHAG', hint: 'Multi-year' },
  {
    icon: <CalendarRange className="h-4 w-4" />,
    label: 'Quarterly',
    hint: 'Quarter-scoped',
  },
  {
    icon: <ClipboardList className="h-4 w-4" />,
    label: 'OP1',
    hint: 'Annual operating plan',
  },
  {
    icon: <ClipboardList className="h-4 w-4" />,
    label: 'OP2',
    hint: 'Annual refresh',
  },
  {
    icon: <HeartPulse className="h-4 w-4" />,
    label: 'Annual & Custom',
    hint: 'Plus your own',
  },
];

export function AboutPage() {
  return (
    <div>
      <PageHeader
        icon={<Info className="h-5 w-5" />}
        title="About Vector"
        description="A tour of what Vector does and how the pieces fit together."
      />

      {/* Hero: what Vector is + principle + the core loop */}
      <Card className="mb-6 overflow-hidden">
        <CardBody className="space-y-5">
          <div className="flex items-center gap-3">
            <Logo size="lg" showTagline />
          </div>

          <p className="max-w-3xl text-sm leading-relaxed text-zinc-300">
            Vector is one place for an engineering team to plan, estimate, and
            ship — from a multi-year goal down to today’s task. It keeps
            planning honest by tracing goals to real sprint work, and it puts a
            grounded AI layer over everything the team records.
          </p>

          <div className="rounded-xl border border-accent/30 bg-accent-soft/20 px-4 py-3">
            <p className="text-sm font-medium text-accent-fg">
              Simple on the surface, intelligent underneath.
            </p>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
              The core loop
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              {CORE_LOOP.map((step, i) => (
                <span key={step} className="flex items-center gap-1.5">
                  <span className="rounded-lg border border-surface-border bg-surface-1 px-2.5 py-1 text-xs font-medium text-zinc-200">
                    {step}
                  </span>
                  {i < CORE_LOOP.length - 1 && (
                    <ArrowRight
                      className="h-3.5 w-3.5 text-zinc-600"
                      aria-hidden="true"
                    />
                  )}
                </span>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Framework highlight strip */}
      <Card className="mb-6">
        <CardBody>
          <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-zinc-100">
            <Target className="h-4 w-4 text-accent" aria-hidden="true" />
            Planning frameworks — including Amazon OP1 / OP2
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {FRAMEWORKS.map((f) => (
              <div
                key={f.label}
                className="rounded-xl border border-surface-border bg-surface-1 px-3 py-2.5"
              >
                <span className="flex items-center gap-1.5 text-sm font-medium text-zinc-200">
                  <span className="text-accent" aria-hidden="true">
                    {f.icon}
                  </span>
                  {f.label}
                </span>
                <p className="mt-0.5 text-xs text-zinc-500">{f.hint}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs leading-relaxed text-zinc-500">
            OP1 is the annual operating plan; OP2 is its annual refresh
            (mid-year re-plan). Both sit alongside BHAG, Quarterly, Annual, and
            Custom so a goal can be expressed in whatever cycle the org plans in.
          </p>
        </CardBody>
      </Card>

      {/* Feature areas */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {SECTIONS.map((section) => (
          <Card key={section.heading}>
            <CardBody className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-1 text-accent">
                  {section.icon}
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-zinc-100">
                    {section.heading}
                  </h2>
                  <p className="mt-0.5 text-sm text-zinc-400">
                    {section.blurb}
                  </p>
                </div>
              </div>
              <ul className="space-y-2.5 border-t border-surface-border pt-3">
                {section.points.map((p) => (
                  <li key={p.label}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">
                      {p.label}
                    </p>
                    <p className="mt-0.5 text-sm leading-relaxed text-zinc-400">
                      {p.detail}
                    </p>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default AboutPage;
